<template>
  <div style="width: 100%">
    <div>
      <PodContextMenu
        v-if="contextMenuPod && contextMenuPod.archived === archiveStatus.NOTARCHIVED"
        v-model="showPodContextMenu"
        :asSearchResult="asSearchResult"
        :positionX="contextMenuX"
        :positionY="contextMenuY"
        :item="contextMenuPod"
        :key="contextMenuX * contextMenuY"
      />

      <ArchivedPodContextMenu
        v-if="contextMenuPod && contextMenuPod.archived === archiveStatus.ARCHIVED"
        v-model="showPodContextMenu"
        :asSearchResult="asSearchResult"
        :positionX="contextMenuX"
        :positionY="contextMenuY"
        :item="contextMenuPod"
        :key="contextMenuX * contextMenuY"
      />

      <NodeContextMenu
        v-if="contextMenuNode && contextMenuNode.archived === archiveStatus.NOTARCHIVED"
        v-model="showNodeContextMenu"
        :positionX="contextMenuX"
        :positionY="contextMenuY"
        :node="contextMenuNode"
        :key="contextMenuX * contextMenuY"
      />

      <ArchivedNodeContextMenu
        v-if="contextMenuNode && contextMenuNode.archived === archiveStatus.ARCHIVED"
        v-model="showNodeContextMenu"
        :positionX="contextMenuX"
        :positionY="contextMenuY"
        :node="contextMenuNode"
        :key="contextMenuX * contextMenuY"
      />
    </div>

    <div class="d-flex flex-wrap">
      <div v-for="item in items" :key="item.id">
        <GridPodItem
          v-if="item.nodeid"
          :item="item"
          :asSearchResult="asSearchResult"
          @showContextMenu="showContextMenuEvent"
          @toggleItemSelection="toggleItemSelectionEvent"
          :style="getListItemStyle(item)"
          class="grid-view-item"
        />

        <GridNodeItem
          v-else
          :item="item"
          :asSearchResult="asSearchResult"
          @showContextMenu="showContextMenuEvent"
          @toggleItemSelection="toggleItemSelectionEvent"
          @droppedMovableItems="$emit('droppedMovableItems', $event);"
          :style="getListItemStyle(item)"
          class="grid-view-item"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useCommonStore } from '@/stores/CommonStore.js'
import { useFilterStore } from '@/stores/FilterStore.js'
import GridNodeItem from './GridNodeItem.vue'
import GridPodItem from './GridPodItem.vue'
import PodContextMenu from '../contextMenus/PodContextMenu.vue'
import NodeContextMenu from '../contextMenus/NodeContextMenu.vue'
import ArchivedNodeContextMenu from '../contextMenus/ArchivedNodeContextMenu.vue'
import ArchivedPodContextMenu from '../contextMenus/ArchivedPodContextMenu.vue'

export default {
  emits: ["resetPaginatedItems", "openedContextMenu", "droppedMovableItems"],
  name: "InfiniteGridView",

  components: {
    GridNodeItem,
    GridPodItem,
    PodContextMenu,
    NodeContextMenu,
    ArchivedNodeContextMenu,
    ArchivedPodContextMenu,
  },

  props: {
    items: {
      type: Array,
      required: true,
    },
    asSearchResult: {
      type: Boolean,
      default: false,
    },
    parentContextMenuStatus: {
      type: Boolean,
      required: false,
    },
  },

  data: function() {
    return {
      // Context menu options
      showPodContextMenu: false,
      showNodeContextMenu: false,
      contextMenuPod: null,
      contextMenuNode: null,
      contextMenuX: 0,
      contextMenuY: 0,
      noDataText: this.$t('routes.node.noFilesAndFolders'),

      anchorItemSelected: null,
    };
  },

  beforeUnmount: function() {
    const filterStore = useFilterStore();
    filterStore.setSortColumn(null);

    this.$emit('resetPaginatedItems');
  },

  watch: {
    parentContextMenuStatus: function(newValue) {
      // hide the current context menu if the context menu on the parent is open
      if (newValue) {
        this.showPodContextMenu = false;
        this.showNodeContextMenu = false;
      }
    }
  },

  methods: {
    showContextMenuEvent(eventData) {
      this.showPodContextMenu = false;
      this.showNodeContextMenu = false;

      this.contextMenuPod = eventData.contextMenuPod;
      this.contextMenuNode = eventData.contextMenuNode;

      this.contextMenuX = eventData.contextMenuX;
      this.contextMenuY = eventData.contextMenuY;

      this.$emit('openedContextMenu', true);

      this.$nextTick(() => {
        if (eventData.contextMenuPod) {
          this.showPodContextMenu = true;
        }

        if (eventData.contextMenuNode) {
          this.showNodeContextMenu = true;
        }
      });
    },
    toggleItemSelectionEvent(eventData) {
      // const indexOfAnchor = this.items.indexOf(this.anchorItemSelected);
      const indexOfItem = this.items.indexOf(eventData.item);
      const indexOfAnchor = this.items.findIndex((item) => item.id === this.anchorItemSelected?.id);

      if (indexOfAnchor < 0) {
        this.anchorItemSelected = null;
      }

      if (eventData.holdingShift) {
        this.deselectAllItems();

        if (this.anchorItemSelected !== null && this.anchorItemSelected.id !== eventData.item.id) {
          const minIndex = Math.min(indexOfAnchor, indexOfItem);
          const maxIndex = Math.max(indexOfAnchor, indexOfItem);

          for (let i = minIndex; i <= maxIndex; i++) {
            this.toggleItemSelection(this.items[i]);
          }

          return;
        }
      }

      this.toggleItemSelection(eventData.item);
      this.anchorItemSelected = eventData.item;
    },
    deselectAllItems() {
      const nodeStore = useNodeStore();
      nodeStore.deselectAllNodes();

      const podStore = usePodStore();
      podStore.deselectAllPods();
    },
    toggleItemSelection(item) {
      if (item.nodeid) {
        const podStore = usePodStore();
        podStore.toggleSelectedPod(item);
      } else {
        const nodeStore = useNodeStore();
        nodeStore.toggleSelection(item);
      }
    },
  },

  computed: {
    archiveStatus() {
      const commonStore = useCommonStore();
      return commonStore.archiveStatus;
    },
    getListItemStyle() {
      return (item) => {
        const style = {};
        if (item.id === this.anchorItemSelected?.id) {
          style['border'] = '1px dashed #999999';
        }

        return style;
      }
    }
  }
};
</script>

<style>
.grid-view-item a { text-decoration: none; font-size: 12px; font-weight: 600;}

.v-theme--dark  .grid-view-item a { color: #FFFFFF; }
.v-theme--light .grid-view-item a { color: #000000; }

.grid-view-item {
  float: left;
  width: 224px;
  margin: 5px;
  padding: 12px;
  height: 194px;
  border-radius: 6px;
}

.grid-view-item > div {
  width: 100%;
}

.v-theme--dark .grid-view-item {
  background-color: #232b36;
}

.v-theme--light .grid-view-item {
  background-color: #FFFFFF;
}

.v-theme--dark .grid-view-item:hover {
  background-color: #373f48;
}
.v-theme--light .grid-view-item:hover {
  background-color: rgba(17, 17, 17,  0.1);
}

.v-theme--dark  .grid-view-item .v-chip {
  background-color: rgba(85, 85, 85, 0.7) !important;
}

.v-theme--light  .grid-view-item .v-chip {
  background-color: rgba(224, 224, 224, 0.7) !important;
}
</style>
